var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import ProjectRelationshipApi from '~/api/project-relationship';
import ProjectsApi from '~/api/projects';
import ProjectsWorkSpaceApi from '~/api/projects-workspace';
import { useUser } from '~/providers/user';
import Permissions from '~/settings/permissions';
import { MenuStates } from './enums';
const AppSidebarContext = createContext({});
const AppSidebarProvider = (props) => {
    const navigate = useNavigate();
    const { activeOrganization } = useUser();
    const { pathname: path } = useLocation();
    const { activeTab, folderId } = useParams();
    const [activeRoute, setActiveRoute] = useState('/');
    const [activeProjectWorkspaceId, setActiveProjectWorkspaceId] = useState(null);
    const [expandedItem, setExpandedItem] = useState(null);
    const [projectsWorkspaces, setProjectWorkspaces] = useState(null);
    const [isArchivedVisible, setIsArchivedVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isCollapsed, setIsCollapsed] = useLocalStorage('sidebar-collapsed', false);
    const { permissions } = useUser();
    const menuStates = [
        MenuStates.RecentActivity,
        MenuStates.ProjectsWorkspace,
    ];
    useEffect(() => {
        setActiveRoute(path);
        if (!path.includes(MenuStates.ProjectsWorkspace)) {
            setIsCollapsed(true);
        }
        for (const page of menuStates) {
            if (path.includes(page)) {
                setExpandedItem(page);
                return;
            }
        }
    }, [path]);
    useEffect(() => {
        if (folderId) {
            setActiveProjectWorkspaceId(folderId);
        }
    }, []);
    useEffect(() => {
        if (folderId) {
            setActiveProjectWorkspaceId(folderId);
        }
    }, [folderId]);
    const fetchProjectWorkSpace = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!permissions.includes(Permissions.VIEW_PROJECTS_WORKSPACE_PAGE))
            return;
        setIsLoading(true);
        const projectWorkspaceList = yield ProjectsWorkSpaceApi.get();
        setProjectWorkspaces(projectWorkspaceList);
        setIsLoading(false);
        return projectWorkspaceList;
    }), [permissions]);
    const updateProject = useCallback((projectId_1, newName_1, ...args_1) => __awaiter(void 0, [projectId_1, newName_1, ...args_1], void 0, function* (projectId, newName, isArchived = false) {
        yield ProjectsApi.update(projectId, {
            name: newName,
            isArchived: isArchived,
        });
        fetchProjectWorkSpace();
    }), [fetchProjectWorkSpace]);
    const deleteProject = useCallback((projectId) => __awaiter(void 0, void 0, void 0, function* () {
        yield ProjectsWorkSpaceApi.deleteById(projectId);
        fetchProjectWorkSpace();
        if (path.includes(MenuStates.ProjectsWorkspace) && path.includes(projectId)) {
            navigate('/projects-workspace/' + (activeTab !== null && activeTab !== void 0 ? activeTab : 'document-comparison'));
        }
    }), [fetchProjectWorkSpace, path, activeTab]);
    const moveProject = useCallback((projectId, targetProjectId) => __awaiter(void 0, void 0, void 0, function* () {
        if (!projectsWorkspaces)
            return;
        const searchParentNode = (nodeId, nodes, parent) => {
            if (nodeId) {
                for (const node of nodes) {
                    if (node.id === nodeId) {
                        return parent;
                    }
                    else if (node.children && node.children.length > 0) {
                        const found = searchParentNode(nodeId, node.children, node);
                        if (found) {
                            return found;
                        }
                    }
                }
            }
        };
        const parentProject = searchParentNode(projectId, projectsWorkspaces);
        if (parentProject) {
            const relationships = yield ProjectRelationshipApi.get({
                projectIdParent: parentProject.id,
                ProjectIdChild: projectId,
            });
            const relationshipToBeDeleted = relationships.find(r => r.projectChild.id === projectId);
            if (relationshipToBeDeleted) {
                yield ProjectRelationshipApi.deleteById(relationshipToBeDeleted.id);
            }
        }
        if (targetProjectId) {
            yield ProjectRelationshipApi.post({
                projectIdParent: targetProjectId,
                ProjectIdChild: projectId,
            });
        }
        fetchProjectWorkSpace();
    }), [projectsWorkspaces, fetchProjectWorkSpace]);
    useEffect(() => {
        if (activeOrganization === null || activeOrganization === void 0 ? void 0 : activeOrganization.id) {
            fetchProjectWorkSpace();
        }
    }, [activeOrganization, fetchProjectWorkSpace]);
    const toggleExpandedItem = (itemName) => {
        if (expandedItem === itemName) {
            setExpandedItem(null);
            return;
        }
        setExpandedItem(itemName);
    };
    const createProject = useCallback((folderName, parentFolderId) => __awaiter(void 0, void 0, void 0, function* () {
        const newProject = yield ProjectsWorkSpaceApi.post({
            project: {
                name: folderName,
                isArchived: false,
            },
            parentProjectId: parentFolderId !== null && parentFolderId !== void 0 ? parentFolderId : '',
        });
        yield fetchProjectWorkSpace();
        navigate('/projects-workspace/' + (activeTab !== null && activeTab !== void 0 ? activeTab : 'document-comparison') + `/${newProject.id}`);
        setActiveProjectWorkspaceId(newProject.id);
    }), [fetchProjectWorkSpace]);
    const nav = {
        activeRoute,
        expandedItem,
        isCollapsed,
        projectsWorkspaces,
        isLoading,
        activeProjectWorkspaceId,
        isArchivedVisible,
        setIsArchivedVisible,
        setActiveProjectWorkspaceId,
        createProject,
        fetchProjectWorkSpace,
        deleteProject,
        moveProject,
        updateProject,
        setIsCollapsed,
        setActiveRoute,
        toggleExpandedItem,
    };
    return React.createElement(AppSidebarContext.Provider, Object.assign({ value: nav }, props));
};
const useAppSidebar = () => {
    const context = useContext(AppSidebarContext);
    if (!context)
        throw new Error('AppSidebarContext must be used inside of a AppSidebarProvider');
    return context;
};
export { AppSidebarProvider, useAppSidebar, };
